import { useEffect, useState } from "react";

/**
 * Formats a remaining-milliseconds value as `m:ss`. Negative and sub-second values both floor to
 * "0:00" so an elapsed deadline never renders as a negative clock.
 */
export function formatCountdown(msRemaining: number): string {
  const totalSeconds = Math.max(0, Math.floor(msRemaining / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

/**
 * Counts down to an absolute ISO deadline, returning the milliseconds remaining (clamped at 0), or
 * null when there is no deadline to track.
 *
 * Each tick recomputes from the deadline rather than decrementing a counter: browsers throttle
 * timers in background tabs and stop them entirely while a device sleeps, so a decrementing clock
 * drifts arbitrarily far behind the reservation it is supposed to represent. Recomputing also means
 * `visibilitychange` can refresh the value immediately on return, instead of showing up to a second
 * of stale time.
 */
export function useCountdown(deadlineIso: string | undefined): number | null {
  const deadlineMs = deadlineIso ? new Date(deadlineIso).getTime() : Number.NaN;
  const hasDeadline = !Number.isNaN(deadlineMs);

  const [msRemaining, setMsRemaining] = useState<number | null>(() =>
    hasDeadline ? Math.max(0, deadlineMs - Date.now()) : null,
  );

  useEffect(() => {
    if (!hasDeadline) {
      setMsRemaining(null);
      return;
    }

    const sync = () => setMsRemaining(Math.max(0, deadlineMs - Date.now()));

    sync();
    const intervalId = window.setInterval(sync, 1000);
    document.addEventListener("visibilitychange", sync);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", sync);
    };
  }, [deadlineMs, hasDeadline]);

  return msRemaining;
}
